import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Container from '../../../../core/components/atoms/Container';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';
import PageHeader from '../../../../core/components/molecules/Header';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { Colors } from '../../../../core/models/disciplines';
import { getDisciplineName } from '../../../../core/utils/new_discipline';
import ContentButtonGroup from '../../components/ContentButtonGroup';
import SubjectSelectSection from '../../components/SubjectsSelect';
import ExerciseList from '../../components/exercises';
import LessonList from '../../components/lessons';
import MaterialList from '../../components/materials';
import { useDropdownHook } from '../../hooks/useDropdownHook';
import { useModule } from '../../hooks/useModule';
import { useSubject } from '../../hooks/useSubject';
import { useExerciseStore } from '../../stores/exercisesStore';
import { useFilterConfigStore } from '../../stores/filterConfig';
import { useNewModuleStore } from '../../stores/newModuleStore';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import { getModuleDescription, getModuleLabel } from '../../utils/contentHelper';

export default function ContentPage() {
  const { module, discipline, type } = useParams();
  const { pathname } = useLocation();
  const prevPathname = useRef(pathname);

  const { mutate, isPending } = useSubject();
  const { mutate: getModules, isPending: loadingModules } = useModule();

  const { modules } = useNewModuleStore();
  const { subjects, setSelectedSubjects } = useNewSubjectStore();
  const { resetExercises } = useExerciseStore();

  const { array: dropdownItems } = useDropdownHook(modules || [], pathname, module || '');

  useEffect(() => {
    if(discipline) {
      getModules(discipline)
    }
  }, [discipline])

  useEffect(() => {
    resetExercises()
  },[discipline, module])
  
  useEffect(() => {
    if(module) {
      const actualModule = modules.find(m => m.slug === module);

      if(actualModule && subjects && subjects[0] && subjects[0].subarea !== actualModule?.id) {
        setSelectedSubjects([])
      }

      mutate(module);
    }
  }, [module])

  useEffect(() => {
    if (pathname !== prevPathname.current) {
      const isCurrentPathExercise = pathname.endsWith('/exercicio');
      const isPrevPathExercise = prevPathname.current.endsWith('/exercicio');

      if (isCurrentPathExercise || isPrevPathExercise) {
        useExerciseStore.getState().resetExercises();
        useExerciseStore.getState().clearFilters();
        useFilterConfigStore.getState().resetFilters();
      }

      prevPathname.current = pathname;
    }
  }, [pathname]);

  const contentUrl = `/disciplinas/${discipline}/modulo/${module}`;

  const genericLabel = () => {
    if(discipline) {
      return getDisciplineName(discipline);
    }

    return '-'
  };

  const showContent = () => {
    if (type === 'exercicio') {
      return (
        <ExerciseList
          module={module || ''}
          discipline={discipline as Colors}
          url={contentUrl}
        />
      );
    }

    if (type === 'materiais') {
      return (
        <MaterialList
          module={module || ''}
          discipline={discipline as Colors}
          url={contentUrl}
        />
      );
    }

    return (
      <LessonList
        module={module || ''}
        discipline={discipline as Colors}
        url={contentUrl}
      />
    );
  };

  return (
    <Container direction='col' gap='xxl'>
      <Breadcrumbs
        items={[
          {
            label: genericLabel().replace('Humana', ''),
            href: `/disciplinas/${discipline}`,
          },
          {
            label: getModuleLabel(module || ''),
            href: '#',
          },
        ]}
      />
      <PageHeader
        module='Módulo'
        title={getModuleLabel(module || '')}
        isDropdown
        dropdownItems={dropdownItems}
        className='text-left'
        loading={(!modules || modules?.length === 0) && loadingModules}
        isDiscipline
      >
        <Typography>
          {getModuleDescription(module || '')}
        </Typography>
      </PageHeader>
      <ContentButtonGroup type={type ?? ''} contentUrl={contentUrl} />
      {isPending ? <BrainLoadingWrapper /> : (
        <>
          <SubjectSelectSection type={type ?? ''}/>
          {showContent()}
        </>
      )}
    </Container>
  );
}
